import React, { useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useEmptyCallback } from 'utilities/hooks.util';
import {
  DataTable,
  DataTableSkeleton,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableContainer,
  TableCell,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Pagination,
  PaginationSkeleton,
  DataTableCustomRenderProps
} from 'carbon-components-react';
import { ChevronRight16 } from '@carbon/icons-react';
import Util from 'utilities/helper.util';
import { APP_URL } from 'types';
import {
  LocalStoreContext,
  LocalDispatchContext
} from '../../localStore/context';
import { getUsersStart } from '../../localStore/actions';
import { toLocalDateTimeFormat } from 'utilities/helper.util';

const PageSizes = [10, 20, 50];

const UsersList: React.FC<Props> = ({ setSelectedUserId, selectedUserId }) => {
  const location = useLocation<{ remain: boolean }>();
  const localStore = useContext(LocalStoreContext);
  const localDispatch = useContext(LocalDispatchContext);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageOffset, setPageOffset] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [filteredUsersList, setFilteredUsersList] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const { users, getUsersStatus, reloadTrigger } = localStore;

  const handlePageChange = ({
    page,
    pageSize
  }: {
    page: number;
    pageSize: number;
  }) => {
    setPageOffset((page - 1) * pageSize);
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const getUsersData = useEmptyCallback((queryParams: any) => {
    localDispatch(getUsersStart(queryParams));
  });

  const debouncedFunction = Util.debounce(async (args: string[]) => {
    const text = args[0];
    setSearchText(text);
    setPageOffset(0);
    setCurrentPage(1);
  }, 250);

  const handleOnClickRow = (id: any) => {
    setSelectedUserId(id);
  };

  useEffect(() => {
    const filterArr = [];
    if (searchText) {
      filterArr.push(`name:%${searchText}%`);
    }
    const queryParams = {
      filter: filterArr.length > 0 ? filterArr.join(',') : undefined,
      fields:
        'id,first_name,last_name,last_login,enquiries_last_30_days,reg_email',
      limit: pageSize,
      order: 'first_name',
      offset: pageOffset
    };
    getUsersData(queryParams);
  }, [reloadTrigger, pageSize, pageOffset, getUsersData, searchText]);

  useEffect(() => {
    if (users) {
      setTotalItems(users.count);
      const filteredList = users.data?.map((user: any) => {
        const listObj = {
          id: user.id?.toString(),
          firstName: user.first_name,
          lastName: user.last_name,
          emailAddress: user.reg_email,
          lastLogin: toLocalDateTimeFormat(user.last_login),
          enquiriesLast30Days:
            user.Enquiry?.length > 0
              ? user.Enquiry?.length +
                ` ${user.Enquiry?.length > 1 ? 'enquiries' : 'enquiry'} `
              : 'None',
          details: <ChevronRight16 />
        };
        return listObj;
      });
      setFilteredUsersList(filteredList);
    }
  }, [users, getUsersStatus]);

  useEffect(() => {
    if (location.pathname === APP_URL.SYSADMIN_ORG && !location.state?.remain) {
      setPageOffset(0);
      setCurrentPage(1);
    }
  }, [location]);

  const headers = [
    { key: 'firstName', header: 'First name' },
    { key: 'lastName', header: 'Last name' },
    { key: 'emailAddress', header: 'Email address' },
    { key: 'enquiriesLast30Days', header: 'Enquiries last 30 days' },
    { key: 'lastLogin', header: 'Last login' },
    { key: 'details', header: '' }
  ];

  return (
    <div
      id="SysAdminFreePlanUsersList"
      data-testid="SysAdminFreePlanUsersList"
      className={`sys-admin-free-plan-users-list`}
    >
      <div className="page-title-container">
        <div className="page-title">Free plan users</div>
      </div>
      <DataTable rows={filteredUsersList} headers={headers}>
        {({
          rows,
          getTableProps,
          getRowProps,
          getHeaderProps,
          getToolbarProps
        }: DataTableCustomRenderProps) => {
          return (
            <TableContainer>
              <TableToolbar
                {...getToolbarProps()}
                aria-label="data table toolbar"
              >
                <TableToolbarContent>
                  <TableToolbarSearch
                    onChange={(e) =>
                      debouncedFunction(e.currentTarget?.value || '')
                    }
                    persistent
                    placeholder="Search"
                    defaultValue={searchText}
                  />
                </TableToolbarContent>
              </TableToolbar>
              {getUsersStatus !== 'fetching' ? (
                <Table {...getTableProps()}>
                  <TableHead>
                    <TableRow>
                      {headers.map((header, i) => (
                        <TableHeader {...getHeaderProps({ header })} key={i}>
                          {header.header}
                        </TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <>
                      {rows.length > 0 ? (
                        <>
                          {rows.map((row) => {
                            return (
                              <TableRow
                                {...getRowProps({ row })}
                                key={row.id}
                                onClick={() => handleOnClickRow(Number(row.id))}
                                isSelected={selectedUserId === Number(row.id)}
                              >
                                {row.cells.map((cell) => (
                                  <TableCell key={cell.id}>
                                    {cell.value}
                                  </TableCell>
                                ))}
                              </TableRow>
                            );
                          })}
                        </>
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6}>No Record</TableCell>
                        </TableRow>
                      )}
                    </>
                  </TableBody>
                </Table>
              ) : (
                <DataTableSkeleton
                  headers={headers}
                  rowCount={3}
                  showHeader={false}
                  showToolbar={false}
                />
              )}
            </TableContainer>
          );
        }}
      </DataTable>
      {getUsersStatus !== 'fetching' ? (
        <Pagination
          size="lg"
          onChange={(data) => handlePageChange(data)}
          backwardText="Previous page"
          forwardText="Next page"
          itemsPerPageText="Display:"
          page={currentPage}
          pageNumberText="Page Number"
          pageSize={pageSize}
          pageSizes={PageSizes}
          totalItems={totalItems}
        />
      ) : (
        <PaginationSkeleton />
      )}
    </div>
  );
};

type Props = {
  setSelectedUserId: Function;
  selectedUserId: number;
};

export default React.memo(UsersList);
